import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { RideProvider } from '@/context/RideContext';

// Pages
import WelcomePage from '@/pages/WelcomePage';
import OwnerLoginPage from '@/pages/owner/OwnerLoginPage';
import OwnerDashboard from '@/pages/owner/OwnerDashboard';
import OwnerUsers from '@/pages/owner/OwnerUsers';
import OwnerDrivers from '@/pages/owner/OwnerDrivers';
import OwnerRides from '@/pages/owner/OwnerRides';
import OwnerFinance from '@/pages/owner/OwnerFinance';
import OwnerSettings from '@/pages/owner/OwnerSettings';
import OwnerSupport from '@/pages/owner/OwnerSupport';

import PassengerLogin from '@/pages/passenger/PassengerLogin';
import PassengerRegister from '@/pages/passenger/PassengerRegister';
import PassengerHome from '@/pages/passenger/PassengerHome';
import PassengerProfile from '@/pages/passenger/PassengerProfile';
import PassengerWallet from '@/pages/passenger/PassengerWallet';
import PassengerRides from '@/pages/passenger/PassengerRides';
import PassengerRide from '@/pages/passenger/PassengerRide';

import DriverLogin from '@/pages/driver/DriverLogin';
import DriverRegister from '@/pages/driver/DriverRegister';
import DriverHome from '@/pages/driver/DriverHome';
import DriverProfile from '@/pages/driver/DriverProfile';
import DriverEarnings from '@/pages/driver/DriverEarnings';
import DriverDocuments from '@/pages/driver/DriverDocuments';
import DriverRide from '@/pages/driver/DriverRide';

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedTypes: Array<'passenger' | 'driver' | 'owner' | 'admin'>;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedTypes }) => {
  const { currentUser, userType, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && (!currentUser || !userType || !allowedTypes.includes(userType as any))) {
      navigate('/');
    }
  }, [currentUser, userType, isLoading, allowedTypes, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!currentUser || !userType || !allowedTypes.includes(userType as any)) {
    return null;
  }

  return <>{children}</>;
};

// App content with routes
const AppContent: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<WelcomePage />} />
        
        {/* Owner Routes */}
        <Route path="/owner/login" element={<OwnerLoginPage />} />
        <Route 
          path="/owner/dashboard" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/owner/users" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerUsers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/owner/drivers" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerDrivers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/owner/rides" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerRides />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/owner/finance" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerFinance />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/owner/settings" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/owner/support" 
          element={
            <ProtectedRoute allowedTypes={['owner']}>
              <OwnerSupport />
            </ProtectedRoute>
          } 
        />

        {/* Passenger Routes */}
        <Route path="/passenger/login" element={<PassengerLogin />} />
        <Route path="/passenger/register" element={<PassengerRegister />} />
        <Route 
          path="/passenger/home" 
          element={
            <ProtectedRoute allowedTypes={['passenger']}>
              <RideProvider>
                <PassengerHome />
              </RideProvider>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/passenger/profile" 
          element={
            <ProtectedRoute allowedTypes={['passenger']}>
              <PassengerProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/passenger/wallet" 
          element={
            <ProtectedRoute allowedTypes={['passenger']}>
              <PassengerWallet />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/passenger/rides" 
          element={
            <ProtectedRoute allowedTypes={['passenger']}>
              <PassengerRides />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/passenger/ride/:rideId" 
          element={
            <ProtectedRoute allowedTypes={['passenger']}>
              <RideProvider>
                <PassengerRide />
              </RideProvider>
            </ProtectedRoute>
          } 
        />

        {/* Driver Routes */}
        <Route path="/driver/login" element={<DriverLogin />} />
        <Route path="/driver/register" element={<DriverRegister />} />
        <Route 
          path="/driver/home" 
          element={
            <ProtectedRoute allowedTypes={['driver']}>
              <RideProvider>
                <DriverHome />
              </RideProvider>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/driver/profile" 
          element={
            <ProtectedRoute allowedTypes={['driver']}>
              <DriverProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/driver/earnings" 
          element={
            <ProtectedRoute allowedTypes={['driver']}>
              <DriverEarnings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/driver/documents" 
          element={
            <ProtectedRoute allowedTypes={['driver']}>
              <DriverDocuments />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/driver/ride/:rideId" 
          element={
            <ProtectedRoute allowedTypes={['driver']}>
              <RideProvider>
                <DriverRide />
              </RideProvider>
            </ProtectedRoute>
          } 
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
